import {z} from 'zod'
import i18n from './translations/i18n'
import {Palette} from '@/theme/style'

export type Shade = keyof Palette

export type ErrorResponseData = {
    message: string
    code: string
}

export type FormErrorResponse = {
    field: string
    rule: string
}

export const PaginationParams = z.object({
    page: z.number(),
    limit: z.number()
})
export type PaginationParams = z.infer<typeof PaginationParams>

export const ResponseType = z.enum(['small', 'extended'])
export type ResponseType = z.infer<typeof ResponseType>

export const PaginationResponse = z.object({
    total: z.number(),
    lastPage: z.number(),
    perPage: z.number(),
    currentPage: z.number()
})
export type PaginationResponse = z.infer<typeof PaginationResponse>

export const MedicalReportGroup = z.enum(['therapistEvaluation', 'teamEvaluation', 'medicalEvaluation'])
export type MedicalReportGroup = z.infer<typeof MedicalReportGroup>

/**
 * This make easier to handle cases where the server provides no meaningful data (204 No Content)
 * and Axios interprets the response as an empty string ("")
 */
export const NoContent = z.literal('').transform(() => null)
export type NoContent = z.infer<typeof NoContent>

export const AslDistrict = z.object({
    id: z.string(),
    name: z.string(),
    asl: z.object({
        id: z.string(),
        name: z.string()
    })
})
export type AslDistrict = z.infer<typeof AslDistrict>

export const District = z.object({
    id: z.coerce.string(),
    name: z.string(),
    aslName: z.string()
})
export type District = z.infer<typeof District>

export const Coordinates = z.object({
    latitude: z.string(),
    longitude: z.string()
})
export type Coordinates = z.infer<typeof Coordinates>

export const Country = z.object({
    id: z.string(),
    name: z.string()
})
export type Country = z.infer<typeof Country>

export const Province = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    acronym: z.string()
})
export type Province = z.infer<typeof Province>

export const City = z.object({
    id: z.string(),
    name: z.string(),
    latitude: z.string().optional(),
    longitude: z.string().optional(),
    province: Province.optional()
})
export type City = z.infer<typeof City>

export const DateRange = z.object({
    startDate: z.string().optional(),
    endDate: z.string().optional()
})
export type DateRange = z.infer<typeof DateRange>

export const DateRangeLabels = z
    .enum(['today', 'yesterday', 'last_week', 'last_month', 'last_3_months', 'last_6_months', 'last_year'])
    .default('last_month')
export type DateRangeLabels = z.infer<typeof DateRangeLabels>

export const PatientAgeRange = z.enum(['adults', 'evolutionary_age', 'evolutionary_age_adults'])
export type PatientAgeRange = z.infer<typeof PatientAgeRange>

export const MaritalStatus = z.enum([
    'single',
    'married',
    'separated',
    'divorced',
    'widowed',
    'civil_union',
    'former_civil_union_deceased_partner',
    'former_civil_union_dissolution',
    'not_recorded'
])
export type MaritalStatus = z.infer<typeof MaritalStatus>

export const Municipality = z.object({
    id: z.string(),
    name: z.string()
})
export type Municipality = z.infer<typeof Municipality>

export const Contact = z.object({
    id: z.string().min(1).optional(),
    type: z.enum(['mobile_phone', 'email', 'telephone']),
    contact: z.string({required_error: i18n.t('errors:missing_data')}).min(1, {message: i18n.t('errors:missing_data')}),
    label: z.string().nullish()
})
export type Contact = z.infer<typeof Contact>

export const UserDetailsUrlParams = z.object({id: z.string().min(1)})

export const WorkSector = z.enum(['domiciliary', 'outpatient', 'outpatient_domiciliary'])
export type WorkSector = z.infer<typeof WorkSector>

export const TreatmentRequestEligibilityStatus = z.enum([
    'patient-deceased',
    'patient-on-shared-waiting-list',
    'resident-outside-the-region',
    'pri-in-progress',
    'request-already-exists'
])
export type TreatmentRequestEligibilityStatus = z.infer<typeof TreatmentRequestEligibilityStatus>

export const UploadedDocument = z.object({
    id: z.string(),
    url: z.string().url(),
    name: z.string().optional()
})
export type UploadedDocument = z.infer<typeof UploadedDocument>

export const MedicalRecord = z.object({
    id: z.string().min(1),
    code: z.number()
})
export type MedicalRecord = z.infer<typeof MedicalRecord>

export const TableFilterOperator = z.enum([
    '<',
    '<=',
    '=',
    '!=',
    '>=',
    '>',
    'in',
    'not in',
    'empty',
    'not empty',
    'is',
    'is not',
    'like'
])
export type TableFilterOperator = z.infer<typeof TableFilterOperator>

export const TableFilterKey = z.enum([
    'status',
    'sector',
    'birthDate',
    'createdAt',
    'lastLoginAt',
    'roleId',
    'specializationId',
    'serviceType',
    'outRegion',
    'residenceAslDistrictId',
    'residenceCityId',
    'residenceProvinceId',
    'authorizationDate',
    'authorization',
    'type',
    'score',
    'acceptedAt',
    'rejectedAt',
    'rejectedReason',
    'therapistId',
    'referringDoctorId',
    'diagnosisId',
    'startDate',
    'expirationDate',
    'endDate',
    'code',
    'durationDays',
    'absenceDays',
    'suspensionDays',
    'progressStatus',
    'completedAccessesPerCent',
    'openingExportedAt',
    'openingTransmissionAt',
    'closingExportedAt',
    'closingTransmissionAt',
    'neuromuscularDiagnosisId',
    'mobilePhone',
    'telephone',
    'email',
    'projectStatus',
    'taxCode',
    'date',
    'communicatedOnTime',
    'projectCode',
    'projectStartDate',
    'projectExpirationDate',
    'groupName',
    'accessType',
    'remappedSiarAccessType',
    'aslId',
    'report',
    'rateCode',
    'access_100',
    'absence_20',
    'absence_50',
    'absence_nr',
    'consultantId',
    'patientId',
    'isRemote',
    'patientAgeRange',
    'contractType',
    'assistiveRegime',
    'isSuspended',
    'consultant',
    'time',
    'consultantFullName',
    'contactEmail',
    'specializationGroup',
    'isRenewed',
    'age',
    'exportType',
    'dateFrom',
    'dateTo',
    'isExternalStructure',
    'workAreas'
])
export type TableFilterKey = z.infer<typeof TableFilterKey>

export type TableFiltersPayload = {
    filters?: {
        operator: 'and' | 'or'
        fields: Array<{key: TableFilterKey; operator: TableFilterOperator; value?: unknown}>
    }
}

const TableFilter = z
    .object({
        key: TableFilterKey,
        operator: TableFilterOperator.exclude(['is', 'is not']),
        value: z.unknown()
    })
    .superRefine((values, ctx) => {
        if (values.operator != 'empty' && values.operator != 'not empty' && !values.value) {
            ctx.addIssue({
                code: 'custom',
                message: i18n.t('errors:required'),
                path: ['value']
            })
        }
    })

export const TableFiltersValidationSchema = z.object({
    operator: z.enum(['and', 'or']).default('and'),
    fields: z.array(TableFilter)
})
export type TableFiltersValidationSchema = z.infer<typeof TableFiltersValidationSchema>

export const TableOrder = z.object({
    key: z.string().min(1),
    direction: z.enum(['asc', 'desc'])
})
export type TableOrder = z.infer<typeof TableOrder>

export const medicalReportTypes = [
    'start_continuous_project',
    'glo',
    'physiatric_specialist_visit',
    'other',
    'pediatric_visit',
    'renew_continuous_project',
    'occupational_therapy_treatment',
    'social_service_evaluation',
    'psychomotor_evaluation',
    'psychologist_evaluation',
    'orl_visit',
    'professional_educator_evaluation',
    'occupational_therapist_evaluation',
    'neuropsychiatric_specialist_visit',
    'initial_project_assessment',
    'initial_social_service_evaluation',
    'neurological_specialist_visit',
    'psychomotor_treatment',
    'start_cyclical_project',
    'document',
    'cardiology_visit',
    'team_report',
    'cognitive_neuropsychological_treatment',
    'physiotherapy_treatment',
    'pulmonary_visit',
    'neuromotor_treatment',
    'neuromotor_evaluation',
    'speech_therapy_treatment',
    'counseling',
    'speech_therapist_evaluation',
    'nutritional_visit',
    'continuous_project_extension_request',
    'physiotherapist_evaluation',
    'educational_intervention_treatment',
    'previous_extensions',
    'orthopedic_visit',
    'parent_training',
    'psychology_treatment',
    'close_continuous_project',
    'certification_l104',
    'intermediate_project_assessment',
    'close_cyclical_project',
    'renew_close_rehab_project',
    'update_medical_record',
    'medical_interview',
    'therapy_evaluation',
    'cardiac_holter'
    // this type is a special case and it is used to allow the presence of medical reports imported from SIR
] as const

export const MedicalReportTypeKeyName = z.enum(medicalReportTypes)
export type MedicalReportTypeKeyName = z.infer<typeof MedicalReportTypeKeyName>

export const MedicalReportTypeGroupKeyName = z.enum([
    'diaries',
    'medical_reports',
    'attachments',
    'evaluations',
    'social_service_evaluations',
    'specialist_consultations'
])
export type MedicalReportTypeGroupKeyName = z.infer<typeof MedicalReportTypeGroupKeyName>

export const MedicalReportType = z.object({
    id: z.string().min(1),
    name: z.string(),
    keyName: MedicalReportTypeKeyName,
    groupKeyName: MedicalReportTypeGroupKeyName,
    groupName: z.string()
})
export type MedicalReportType = z.infer<typeof MedicalReportType>

export const NotificationPayload = z.object({
    notificationRecipientIds: z.array(z.string()).optional(),
    notificationDetails: z.string().optional()
})
export type NotificationPayload = z.infer<typeof NotificationPayload>

export const DateRangeFilterEnum = z.enum(['month', 'threeMonths', 'sixMonths', 'year'])
export type DateRangeFilterEnum = z.infer<typeof DateRangeFilterEnum>

export const YearQuarterEnum = z.enum(['jan_mar', 'apr_jun', 'jul_sep', 'oct_dec'])
export type YearQuarterEnum = z.infer<typeof YearQuarterEnum>

export const YearSemesterEnum = z.enum(['jan_jun', 'jul_dec'])
export type YearSemesterEnum = z.infer<typeof YearSemesterEnum>
